import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Save, Trash2, Search, RefreshCw, Replace, Edit3, DollarSign, Package, FileText } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListingsManager() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editedItems, setEditedItems] = useState({});
  const [saving, setSaving] = useState({});
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [frProcessing, setFrProcessing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState("retire");
  const [deleting, setDeleting] = useState(false);
  const [lifecycleFilter, setLifecycleFilter] = useState("ACTIVE");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    loadItems();
  }, []);

  const loadItems = async (lifecycle = lifecycleFilter) => {
    setLoading(true);
    setItems([]);
    setSelectedSkus(new Set());
    setEditedItems({});
    const lc = lifecycle === "ALL" ? "" : lifecycle;
    try {
      const res = await axios.get(`${API}/walmart/inventory?max_items=5000&lifecycle_status=${lc}`, { headers, timeout: 180000 });
      setItems(res.data.items);
      toast.success(`Loaded ${res.data.items.length} items`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load inventory");
    }
    setLoading(false);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = !searchQuery ||
      item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.product_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.published_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const toggleSelect = (sku) => {
    const s = new Set(selectedSkus);
    if (s.has(sku)) s.delete(sku); else s.add(sku);
    setSelectedSkus(s);
  };

  const toggleSelectAll = () => {
    if (selectedSkus.size === filteredItems.length) setSelectedSkus(new Set());
    else setSelectedSkus(new Set(filteredItems.map(i => i.sku)));
  };

  const setEdit = (sku, field, value) => {
    setEditedItems(prev => ({
      ...prev,
      [sku]: { ...prev[sku], [field]: value }
    }));
  };

  const getEditValue = (sku, field, original) => {
    return editedItems[sku]?.[field] !== undefined ? editedItems[sku][field] : original;
  };

  const hasEdits = (sku) => {
    return editedItems[sku] && Object.keys(editedItems[sku]).length > 0;
  };

  const saveItem = async (sku) => {
    const edits = editedItems[sku];
    if (!edits) return;
    setSaving(prev => ({ ...prev, [sku]: true }));
    const promises = [];

    if (edits.price !== undefined) {
      promises.push(
        axios.post(`${API}/walmart/update-price`, { sku, price: parseFloat(edits.price) }, { headers })
          .then(() => toast.success(`Price updated for ${sku}`))
          .catch(err => toast.error(`Price update failed for ${sku}: ${err.response?.data?.detail || err.message}`))
      );
    }

    if (edits.quantity !== undefined) {
      promises.push(
        axios.post(`${API}/walmart/update-inventory`, { sku, quantity: parseInt(edits.quantity) }, { headers })
          .then(() => toast.success(`Inventory updated for ${sku}`))
          .catch(err => toast.error(`Inventory update failed for ${sku}: ${err.response?.data?.detail || err.message}`))
      );
    }

    if (edits.product_name !== undefined || edits.description !== undefined) {
      const payload = { sku };
      if (edits.product_name !== undefined) payload.product_name = edits.product_name;
      if (edits.description !== undefined) payload.short_description = edits.description;
      promises.push(
        axios.post(`${API}/walmart/update-item`, payload, { headers })
          .then(res => toast.success(`Item details submitted for ${sku} (Feed: ${res.data.feed_id})`))
          .catch(err => toast.error(`Item update failed for ${sku}: ${err.response?.data?.detail || err.message}`))
      );
    }

    await Promise.allSettled(promises);

    // Update local state
    setItems(prev => prev.map(item => {
      if (item.sku !== sku) return item;
      const updated = { ...item };
      if (edits.price !== undefined) updated.price = parseFloat(edits.price);
      if (edits.quantity !== undefined) updated.quantity = parseInt(edits.quantity);
      if (edits.product_name !== undefined) updated.product_name = edits.product_name;
      return updated;
    }));
    setEditedItems(prev => { const n = { ...prev }; delete n[sku]; return n; });
    setSaving(prev => ({ ...prev, [sku]: false }));
  };

  const runFindReplace = async () => {
    if (!findText) { toast.error("Enter text to find"); return; }
    const matchingSkus = filteredItems
      .filter(i => i.product_name.includes(findText))
      .map(i => i.sku);

    if (matchingSkus.length === 0) {
      toast.error("No items match the find text");
      return;
    }

    setFrProcessing(true);
    try {
      const res = await axios.post(`${API}/walmart/bulk-title-update`, {
        find: findText, replace: replaceText, skus: matchingSkus
      }, { headers, timeout: 120000 });
      toast.success(`${res.data.message} (Feed: ${res.data.feed_id})`);
      // Update local titles
      setItems(prev => prev.map(item => {
        if (matchingSkus.includes(item.sku) && item.product_name.includes(findText)) {
          return { ...item, product_name: item.product_name.replace(findText, replaceText) };
        }
        return item;
      }));
      setShowFindReplace(false);
      setFindText(""); setReplaceText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Find & replace failed");
    }
    setFrProcessing(false);
  };

  const handleBulkDelete = async () => {
    if (selectedSkus.size === 0) return;
    const skus = [...selectedSkus];
    setDeleting(true);
    try {
      if (deleteType === "permanent") {
        const res = await axios.post(`${API}/walmart/permanent-delete`, { skus }, { headers, timeout: 120000 });
        toast.success(`${res.data.message} (Feed: ${res.data.feed_id})`);
      } else {
        const res = await axios.post(`${API}/walmart/delete`, {
          skus, sku_names: Object.fromEntries(items.filter(i => skus.includes(i.sku)).map(i => [i.sku, i.product_name]))
        }, { headers, timeout: 120000 });
        toast.success(`Retirement job started (${res.data.job_id})`);
      }
      setItems(prev => prev.filter(i => !selectedSkus.has(i.sku)));
      setSelectedSkus(new Set());
      setShowDeleteDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
    setDeleting(false);
  };

  const previewFR = findText ? filteredItems.filter(i => i.product_name.includes(findText)).length : 0;

  if (!token) {
    return (
      <div className="app-container">
        <Toaster position="top-right" richColors />
        <header className="main-header">
          <div className="header-logo-centered">
            <img src="/logo.png" alt="MarketMop.com" className="header-logo-img" />
            <div className="logo-text"><h1>MarketMop.com</h1><p>Listings Manager</p></div>
          </div>
        </header>
        <div style={{ textAlign: "center", padding: 80, color: "var(--wm-text-muted)" }}>
          <p>Please <Link to="/" style={{ color: "var(--wm-blue)" }}>sign in</Link> to manage your listings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Toaster position="top-right" richColors />

      <header className="main-header">
        <div className="header-logo-centered">
          <img src="/logo.png" alt="MarketMop.com" className="header-logo-img" />
          <div className="logo-text"><h1>MarketMop.com</h1><p>Listings Manager</p></div>
        </div>
        <div className="header-actions">
          <Link to="/" style={{ textDecoration: "none", color: "var(--wm-blue)", fontWeight: 600, fontSize: "0.875rem", display: "flex", alignItems: "center", gap: 4 }}>
            <ArrowLeft className="w-4 h-4" /> Back to Tools
          </Link>
        </div>
      </header>

      <main className="listings-container" data-testid="listings-page">
        {/* Toolbar */}
        <div className="listings-toolbar" data-testid="listings-toolbar">
          <div className="listings-toolbar-left">
            <Select value={lifecycleFilter} onValueChange={v => { setLifecycleFilter(v); loadItems(v === "ALL" ? "" : v); }}>
              <SelectTrigger data-testid="lifecycle-filter" style={{ width: 150 }}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="RETIRED">Retired</SelectItem>
                <SelectItem value="ALL">All Statuses</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="published-filter" style={{ width: 150 }}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Published</SelectItem>
                <SelectItem value="PUBLISHED">Published</SelectItem>
                <SelectItem value="UNPUBLISHED">Unpublished</SelectItem>
              </SelectContent>
            </Select>
            <div className="listings-search">
              <Search className="w-4 h-4" />
              <Input data-testid="listings-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by SKU or name..." style={{ border: "none", boxShadow: "none", paddingLeft: 0 }} />
            </div>
          </div>
          <div className="listings-toolbar-right">
            <Button variant="outline" onClick={() => loadItems()} disabled={loading} data-testid="refresh-listings-btn" style={{ gap: 6 }}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="outline" onClick={() => setShowFindReplace(true)} data-testid="find-replace-btn" style={{ gap: 6 }}>
              <Replace className="w-4 h-4" /> Find & Replace
            </Button>
            {selectedSkus.size > 0 && (
              <Button onClick={() => setShowDeleteDialog(true)} data-testid="bulk-delete-btn" style={{ gap: 6, background: "var(--wm-danger)", color: "#fff" }}>
                <Trash2 className="w-4 h-4" /> Delete {selectedSkus.size} Items
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="listings-stats">
          <span>{filteredItems.length} items</span>
          {selectedSkus.size > 0 && <span style={{ color: "var(--wm-blue)", fontWeight: 600 }}>{selectedSkus.size} selected</span>}
          {Object.keys(editedItems).length > 0 && <span style={{ color: "#F08C00", fontWeight: 600 }}>{Object.keys(editedItems).length} unsaved changes</span>}
        </div>

        {/* Table */}
        {loading ? (
          <div className="listings-loading"><Loader2 className="w-6 h-6 animate-spin" /> Loading inventory...</div>
        ) : items.length === 0 ? (
          <div className="listings-empty">
            <Package className="w-8 h-8" style={{ margin: "0 auto 12px", opacity: 0.4 }} />
            <p>No items loaded. Click Refresh to load your Walmart inventory.</p>
          </div>
        ) : (
          <div className="listings-table-wrap">
            <table className="data-table listings-table" data-testid="listings-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}><Checkbox checked={selectedSkus.size === filteredItems.length && filteredItems.length > 0} onCheckedChange={toggleSelectAll} /></th>
                  <th style={{ width: 120 }}>SKU</th>
                  <th>Title</th>
                  <th style={{ width: 100 }}>Price</th>
                  <th style={{ width: 80 }}>Qty</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 100 }}>Published</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => {
                  const edited = hasEdits(item.sku);
                  return (
                    <tr key={item.sku} className={`${selectedSkus.has(item.sku) ? "selected-row" : ""} ${edited ? "edited-row" : ""}`} data-testid={`listing-row-${item.sku}`}>
                      <td><Checkbox checked={selectedSkus.has(item.sku)} onCheckedChange={() => toggleSelect(item.sku)} /></td>
                      <td className="font-mono" style={{ fontSize: "0.75rem" }}>{item.sku}</td>
                      <td>
                        <Input
                          data-testid={`edit-title-${item.sku}`}
                          value={getEditValue(item.sku, "product_name", item.product_name)}
                          onChange={e => setEdit(item.sku, "product_name", e.target.value)}
                          className="listings-inline-input"
                          style={{ fontSize: "0.8125rem" }}
                        />
                      </td>
                      <td>
                        <Input
                          data-testid={`edit-price-${item.sku}`}
                          type="number"
                          step="0.01"
                          value={getEditValue(item.sku, "price", item.price ?? "")}
                          onChange={e => setEdit(item.sku, "price", e.target.value)}
                          className="listings-inline-input"
                          style={{ width: 85, fontSize: "0.8125rem" }}
                        />
                      </td>
                      <td>
                        <Input
                          data-testid={`edit-qty-${item.sku}`}
                          type="number"
                          value={getEditValue(item.sku, "quantity", item.quantity ?? "")}
                          onChange={e => setEdit(item.sku, "quantity", e.target.value)}
                          className="listings-inline-input"
                          style={{ width: 70, fontSize: "0.8125rem" }}
                        />
                      </td>
                      <td><span className={`status-badge status-${item.lifecycle_status?.toLowerCase()}`}>{item.lifecycle_status}</span></td>
                      <td><span className={`status-badge status-${item.published_status?.toLowerCase()}`}>{item.published_status}</span></td>
                      <td>
                        {edited && (
                          <Button size="sm" onClick={() => saveItem(item.sku)} disabled={saving[item.sku]} data-testid={`save-item-${item.sku}`} className="action-btn primary-btn" style={{ fontSize: "0.6875rem", padding: "4px 10px", gap: 4 }}>
                            {saving[item.sku] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Find & Replace Dialog */}
      <Dialog open={showFindReplace} onOpenChange={setShowFindReplace}>
        <DialogContent style={{ maxWidth: 500 }}>
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}><Replace className="w-5 h-5" style={{ color: "var(--wm-blue)" }} /> Find & Replace in Titles</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "8px 0" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "var(--wm-text-secondary)", marginBottom: 4 }}>Find</label>
              <Input data-testid="fr-find" value={findText} onChange={e => setFindText(e.target.value)} placeholder="Text to find in titles..." />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "var(--wm-text-secondary)", marginBottom: 4 }}>Replace With</label>
              <Input data-testid="fr-replace" value={replaceText} onChange={e => setReplaceText(e.target.value)} placeholder="Replacement text..." />
            </div>
            {findText && (
              <div style={{ padding: "10px 14px", background: previewFR > 0 ? "var(--wm-blue-light)" : "#FEF3C7", borderRadius: 8, fontSize: "0.875rem" }}>
                {previewFR > 0 ? (
                  <span style={{ color: "var(--wm-blue)" }}><strong>{previewFR}</strong> item{previewFR !== 1 ? "s" : ""} will be updated</span>
                ) : (
                  <span style={{ color: "#92400E" }}>No items match "{findText}"</span>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowFindReplace(false)}>Cancel</Button>
            <Button data-testid="fr-submit" onClick={runFindReplace} disabled={frProcessing || previewFR === 0} className="action-btn primary-btn" style={{ gap: 6 }}>
              {frProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Replace className="w-4 h-4" />}
              {frProcessing ? "Processing..." : "Replace All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent style={{ maxWidth: 450 }}>
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--wm-danger)" }}><Trash2 className="w-5 h-5" /> Delete {selectedSkus.size} Items</DialogTitle>
          </DialogHeader>
          <div style={{ padding: "8px 0" }}>
            <p style={{ fontSize: "0.875rem", color: "var(--wm-text-secondary)", marginBottom: 16 }}>Choose how to remove the selected items:</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label className={`delete-option ${deleteType === "retire" ? "active" : ""}`} data-testid="delete-option-retire">
                <input type="radio" name="deleteType" checked={deleteType === "retire"} onChange={() => setDeleteType("retire")} />
                <div>
                  <strong>Retire</strong>
                  <span>Soft-delete: items are removed from listings but remain in Walmart's system.</span>
                </div>
              </label>
              <label className={`delete-option ${deleteType === "permanent" ? "active" : ""}`} data-testid="delete-option-permanent">
                <input type="radio" name="deleteType" checked={deleteType === "permanent"} onChange={() => setDeleteType("permanent")} />
                <div>
                  <strong>Permanent Delete</strong>
                  <span>Full delete via DELETE_ITEM feed. SKUs are permanently removed from your catalog. Cannot be undone.</span>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-delete-btn" onClick={handleBulkDelete} disabled={deleting} style={{ background: "var(--wm-danger)", color: "#fff", gap: 6 }}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {deleting ? "Deleting..." : `${deleteType === "permanent" ? "Permanently" : ""} Delete ${selectedSkus.size} Items`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
